// The organization filter (AB#49318): with the CS_49318_GLOBAL_ORG_FILTER
// flag on, the organization selector is replaced by a filter that holds one
// organization or none. No filter = every organization of the user.
//
// Pure rules only, so they can be tested without React. AuthorizationContext
// wires them; with the flag off none of them is used.

export const ORG_FILTER_FLAG = "CS_49318_GLOBAL_ORG_FILTER";

// --- Remembered filter ------------------------------------------------------

// Absent = no filter. Independent of the selector's "ao" key, which the
// filter never reads nor writes.
export const ORG_FILTER_STORAGE_KEY = "orgFilter";

export const readStoredOrgFilter = () => {
  try {
    return globalThis.localStorage?.getItem(ORG_FILTER_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

export const storeOrgFilter = (orgGuid) => {
  try {
    if (orgGuid) globalThis.localStorage?.setItem(ORG_FILTER_STORAGE_KEY, orgGuid);
    else globalThis.localStorage?.removeItem(ORG_FILTER_STORAGE_KEY);
  } catch {
    // Storage unavailable: the filter is just not remembered.
  }
};

// Paused: the filter is remembered but every organization is shown, until
// the user resumes it (or sets another one).
export const ORG_FILTER_PAUSED_STORAGE_KEY = "orgFilterPaused";

export const readStoredOrgFilterPaused = () => {
  try {
    return globalThis.localStorage?.getItem(ORG_FILTER_PAUSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const storeOrgFilterPaused = (isPaused) => {
  try {
    if (isPaused) globalThis.localStorage?.setItem(ORG_FILTER_PAUSED_STORAGE_KEY, "1");
    else globalThis.localStorage?.removeItem(ORG_FILTER_PAUSED_STORAGE_KEY);
  } catch {
    // Storage unavailable: the pause is just not remembered.
  }
};

export const forgetStoredOrgFilter = () => {
  storeOrgFilter(null);
  storeOrgFilterPaused(false);
};

// The organization of `organizations` the guid names (in the list's casing),
// or null: a remembered filter the user lost access to is dropped.
export const findOwnOrganizationGuid = (orgGuid, organizations = []) =>
  organizations.find((org) => sameGuid(org.guid, orgGuid))?.guid ?? null;

// --- GUIDs ------------------------------------------------------------------

// IoT.API does not keep one casing for GUIDs across listings.
export const sameGuid = (a, b) =>
  !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

export const guidKey = (guid) => (guid ? String(guid).toLowerCase() : guid);

// --- Feature flags ----------------------------------------------------------

// A `GET features` answer as the store keeps it: { [name]: { isEnabled } }.
export const toFlagMap = (configs = []) => {
  const flags = {};
  (Array.isArray(configs) ? configs : []).forEach((config) => {
    flags[config.name] = { isEnabled: !!config.isEnabled };
  });
  return flags;
};

const flagsOf = (flagsByOrg, orgGuid) =>
  Object.entries(flagsByOrg || {}).find(([guid]) => sameGuid(guid, orgGuid))?.[1] ?? {};

export const isFlagOnFor = (flagsByOrg, orgGuid, flagName) =>
  flagsOf(flagsByOrg, orgGuid)[flagName]?.isEnabled === true;

// The flags of a scope: on when on for at least one of its organizations.
// Every known flag is in the result, so one that is off everywhere is
// explicitly false (the appConfigurations reducer merges into its state).
export const mergeOrganizationFlags = (flagsByOrg, orgGuids = []) => {
  const merged = {};
  Object.values(flagsByOrg || {}).forEach((flags) => {
    Object.keys(flags || {}).forEach((name) => {
      merged[name] = { isEnabled: false };
    });
  });
  orgGuids.forEach((orgGuid) => {
    Object.entries(flagsOf(flagsByOrg, orgGuid)).forEach(([name, value]) => {
      if (value?.isEnabled) merged[name] = { isEnabled: true };
    });
  });
  return merged;
};

// Whether the filter replaces the selector for this user: the flag on for at
// least one of their organizations. A super admin's flags are forced on in
// the UI, and so is the filter: they always have it.
export const isOrgFilterEnabledFor = ({ isSuperAdmin, organizationGuids = [], flagsByOrg = {} }) => {
  if (isSuperAdmin) return true;
  return organizationGuids.some((guid) => isFlagOnFor(flagsByOrg, guid, ORG_FILTER_FLAG));
};

// --- Scope ------------------------------------------------------------------

// The organizations the pages work on: the filtered one, or all of them.
export const scopeOrganizations = (organizations = [], filteredOrgGuid = null) =>
  filteredOrgGuid
    ? organizations.filter((org) => sameGuid(org.guid, filteredOrgGuid))
    : organizations;

export const isInScope = (orgGuid, filteredOrgGuid = null) =>
  !filteredOrgGuid || sameGuid(orgGuid, filteredOrgGuid);

export const filterToScope = (items, filteredOrgGuid, getOrgGuid) => {
  const all = items ?? [];
  return filteredOrgGuid ? all.filter((item) => sameGuid(getOrgGuid(item), filteredOrgGuid)) : all;
};

export const siteOrganizationGuid = (site) => site?.customer?.customerGuid ?? null;

export const spansSeveralOrganizations = (items, getOrgGuid) =>
  new Set((items ?? []).map((item) => guidKey(getOrgGuid(item))).filter(Boolean)).size > 1;

// --- Detail pages (site, gateway, controller) ---------------------------------

// The organizations of the user a detail page can work in: the one owning the
// site, then the one owning the fleet that holds the gateway. Access rights do
// not depend on the choice (IoT.API checks the fleet, else the site); it picks
// the template context and the subscription the page applies.
export const resolveEntityOrganizations = ({
  siteOrgGuid,
  fleetGuid,
  groups = [],
  organizations = [],
}) => {
  const fleetGroup = fleetGuid
    ? groups.find((g) => g.type === "Fleet" && sameGuid(g.guid, fleetGuid))
    : null;
  const fleetOrgGuid = fleetGroup
    ? groups.find((g) => g.id === fleetGroup.parentGroupId)?.guid
    : null;
  return [siteOrgGuid, fleetOrgGuid]
    .map((guid) => findOwnOrganizationGuid(guid, organizations))
    .filter((guid, index, all) => guid && all.indexOf(guid) === index);
};

// The filter when it gives access to the page, else the first candidate.
export const pickPageOrganization = (candidates = [], filteredOrgGuid = null) => {
  if (candidates.length === 0) return null;
  return candidates.find((guid) => sameGuid(guid, filteredOrgGuid)) ?? candidates[0];
};

// --- Pages that work on one organization (API, audit log) ---------------------

// Among the organizations where the page can be used: the filter when it is
// one of them, else the only one there is. Otherwise null: the page asks.
export const pickSingleOrganizationPageOrg = (usableOrganizations = [], filteredOrgGuid = null) =>
  (filteredOrgGuid && findOwnOrganizationGuid(filteredOrgGuid, usableOrganizations)) ||
  (usableOrganizations.length === 1 ? usableOrganizations[0].guid : null);

// --- Organization lists (a super admin sees hundreds of customers) -------------

export const MAX_LISTED_ORGANIZATIONS = 50;

export const searchOrganizations = (
  organizations = [],
  query = "",
  max = MAX_LISTED_ORGANIZATIONS
) => {
  const term = String(query ?? "").trim().toLowerCase();
  const matching = term
    ? organizations.filter((org) => (org.name || "").toLowerCase().includes(term))
    : organizations;
  return {
    items: matching.slice(0, max),
    hiddenCount: Math.max(0, matching.length - max),
  };
};

export const sortByName = (organizations = []) =>
  [...organizations].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

// --- Listings IoT.API serves per customer (alarms, subscriptions) -------------

// IoT.API needs a customer for a non-super-admin, so with no filter they are
// asked once per organization (`perOrganization`). Otherwise one call for
// `customerGuid`: null lists every customer, which only a super admin may do.
export const customerRequestPlan = ({
  isOrgFilterEnabled,
  isSuperAdmin,
  filteredOrgGuid = null,
  activeOrgGuid = null,
}) => {
  if (!isOrgFilterEnabled) {
    return { perOrganization: false, customerGuid: isSuperAdmin ? null : activeOrgGuid };
  }
  if (filteredOrgGuid) return { perOrganization: false, customerGuid: filteredOrgGuid };
  return { perOrganization: !isSuperAdmin, customerGuid: null };
};
